import { AbsoluteFill, interpolate, useCurrentFrame } from "remotion";
import {
  Caption,
  Callout,
  CodePanel,
  COLORS,
  FONTS,
  MetricCounter,
  Panel,
  PipelineGraph,
  SceneTransition,
  SlideTimeBar,
  type PipeNode,
} from "../../coldcompute";
import { SCENE } from "./constants";
import { HOOK, PROBLEM, INTUITION, APPROACH, CODE, PAYOFF, OUTRO } from "./script";

const center: React.CSSProperties = {
  alignItems: "center",
  justifyContent: "center",
  flexDirection: "column",
};

const Kicker: React.FC<{ text: string; color: string }> = ({ text, color }) => (
  <div
    style={{
      fontFamily: FONTS.mono,
      fontSize: 22,
      letterSpacing: "0.22em",
      textTransform: "uppercase",
      color,
      marginBottom: 22,
    }}
  >
    ▍{text}
  </div>
);

const Big: React.FC<{ children: React.ReactNode; size?: number }> = ({
  children,
  size = 92,
}) => (
  <div
    style={{
      fontFamily: FONTS.display,
      fontWeight: 700,
      fontSize: size,
      lineHeight: 0.98,
      letterSpacing: "-0.02em",
      color: COLORS.text,
      textAlign: "center",
      maxWidth: 1500,
    }}
  >
    {children}
  </div>
);

/* 01 — HOOK */
export const Scene01Hook: React.FC = () => {
  const frame = useCurrentFrame();
  const zero = interpolate(frame, [40, 70], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.hook.duration} />
      <SceneTransition from="up">
        <AbsoluteFill style={center}>
          <Kicker text="hybrid search · what broke" color={COLORS.cyan} />
          <Big>
            30% of my relevance score
            <br />
            was returning{" "}
            <span style={{ color: COLORS.red, opacity: 0.35 + zero * 0.65 }}>
              ZERO
            </span>
          </Big>
        </AbsoluteFill>
      </SceneTransition>
      <Caption lines={HOOK} accent="cyan" />
    </AbsoluteFill>
  );
};

/* 02 — PROBLEM */
export const Scene02Problem: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.problem.duration} />
    <SceneTransition from="up">
      <AbsoluteFill style={center}>
        <Kicker text="the silent failure" color={COLORS.cyan} />
        <div style={{ display: "flex", gap: 28, alignItems: "stretch" }}>
          <Panel accent="cyan" title="vector · semantic" width={460}>
            <div style={{ fontFamily: FONTS.mono, fontSize: 22, lineHeight: 1.9, color: COLORS.text }}>
              <div>handleAuth() <span style={{ color: COLORS.dim }}>0.71</span></div>
              <div>loginFlow.ts <span style={{ color: COLORS.dim }}>0.69</span></div>
              <div>sessionUtil <span style={{ color: COLORS.dim }}>0.66</span></div>
              <div style={{ color: COLORS.dim }}>…the exact term, buried ↓</div>
            </div>
          </Panel>
          <Panel accent="red" title="bm25 · keyword (30%)" width={460}>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: 150 }}>
              <div style={{ fontFamily: FONTS.display, fontWeight: 700, fontSize: 64, color: COLORS.red }}>0</div>
              <div style={{ fontFamily: FONTS.mono, fontSize: 18, color: COLORS.dim }}>results · plainto_tsquery AND</div>
            </div>
          </Panel>
        </div>
      </AbsoluteFill>
    </SceneTransition>
    <Caption lines={PROBLEM} accent="cyan" />
  </AbsoluteFill>
);

/* 03 — INTUITION */
export const Scene03Intuition: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.intuition.duration} />
    <SceneTransition from="up">
      <AbsoluteFill style={center}>
        <Kicker text="intuition" color={COLORS.violet} />
        <div style={{ display: "flex", gap: 40, alignItems: "center" }}>
          <Big size={68}>by MEANING</Big>
          <div style={{ fontFamily: FONTS.display, fontSize: 60, color: COLORS.dim }}>+</div>
          <Big size={68}>by EXACT WORDS</Big>
        </div>
        <div style={{ fontFamily: FONTS.body, fontSize: 30, color: COLORS.dim, marginTop: 30 }}>
          two lanes, fused into one ranking
        </div>
      </AbsoluteFill>
    </SceneTransition>
    <Caption lines={INTUITION} accent="violet" />
  </AbsoluteFill>
);

/* 04 — APPROACH */
const PIPE: PipeNode[] = [
  { label: "query", value: "Q", accent: "cyan" },
  { label: "signals", value: "VEC + BM25", sub: "parallel", accent: "green" },
  { label: "fuse", value: "0.7 / 0.3", sub: "over-fetch ×3", accent: "violet" },
  { label: "output", value: "RANK", sub: "top match_count", accent: "amber" },
];
export const Scene04Approach: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.approach.duration} />
    <AbsoluteFill style={{ ...center, padding: "0 90px" }}>
      <Kicker text="migration 009 · hybrid_search" color={COLORS.green} />
      <div style={{ width: 1620 }}>
        <PipelineGraph nodes={PIPE} startFrame={6} stepFrames={22} nodeWidth={210} />
      </div>
    </AbsoluteFill>
    <Caption lines={APPROACH} accent="green" />
  </AbsoluteFill>
);

/* 05 — CODE */
const SQL = `-- 009_hybrid_search_function.sql   (0.7 vector + 0.3 text)
with vector_results as (
  select id, 1 - (embedding_vector <=> q) as vec_sim
  from document_chunks where tenant_id = $1
  order by embedding_vector <=> q limit match_count * 3
),
text_results as (
  select id, ts_rank_cd(search_tsvector,
    plainto_tsquery('english', query_text)) as text_rank
)
select id,
  (vec_sim * 0.7) + (coalesce(text_rank, 0) * 0.3) as search_rank
from vector_results left join text_results using (id)
order by search_rank desc limit match_count;`;
export const Scene05Code: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.code.duration} />
    <AbsoluteFill style={{ alignItems: "center", justifyContent: "center", paddingBottom: 40 }}>
      <CodePanel
        code={SQL}
        language="sql"
        filename="009_hybrid_search_function.sql"
        highlightLines={[9, 12]}
        revealFrames={300}
        fontSize={24}
        width={1240}
      />
    </AbsoluteFill>
    <Caption lines={CODE} accent="green" />
  </AbsoluteFill>
);

/* 06 — PAYOFF */
export const Scene06Payoff: React.FC = () => {
  const frame = useCurrentFrame();
  const c1 = interpolate(frame, [120, 150], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  const c2 = interpolate(frame, [240, 270], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.payoff.duration} />
      <AbsoluteFill style={center}>
        <Kicker text="code-query accuracy" color={COLORS.lime} />
        <div style={{ display: "flex", alignItems: "baseline", gap: 18 }}>
          <span style={{ fontFamily: FONTS.display, fontWeight: 700, fontSize: 60, color: COLORS.dim }}>~75%</span>
          <span style={{ fontFamily: FONTS.display, fontSize: 56, color: COLORS.dim }}>→</span>
          <MetricCounter value={90} from={75} startFrame={10} durationFrames={60} accent="lime" suffix="%" fontSize={150} />
        </div>
        <div style={{ display: "flex", gap: 30, marginTop: 38 }}>
          <div style={{ opacity: c1 }}>
            <Callout label="mig 033 · OR-tsquery" accent="green" width={360}>
              BM25 results: <b style={{ color: COLORS.text }}>0 → 15–20</b>
            </Callout>
          </div>
          <div style={{ opacity: c2 }}>
            <Callout label="mig 034 · camelCase split" accent="lime" width={360}>
              broke the <b style={{ color: COLORS.text }}>85% ceiling</b> → peak 90%
            </Callout>
          </div>
        </div>
      </AbsoluteFill>
      <Caption lines={PAYOFF} accent="lime" />
    </AbsoluteFill>
  );
};

/* 07 — OUTRO */
export const Scene07Outro: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.outro.duration} />
    <SceneTransition from="up">
      <AbsoluteFill style={center}>
        <Big size={68}>
          Vector misses keywords.
          <br />
          Keyword misses meaning.
          <br />
          <span style={{ color: COLORS.cyan }}>Use both — and check both fire.</span>
        </Big>
        <div style={{ fontFamily: FONTS.mono, fontSize: 24, letterSpacing: "0.24em", color: COLORS.dim, marginTop: 44 }}>
          EAKC · grounded retrieval
        </div>
      </AbsoluteFill>
    </SceneTransition>
    <Caption lines={OUTRO} accent="cyan" />
  </AbsoluteFill>
);

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
